import React, { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import SegmentFinder from "@/components/SegmentFinder";
import {
  adminStats, adminTransactions, adminSweeps, adminIntakes, adminEnquiries,
  adminAuditReports, adminReviewAuditReport, adminDownloadAuditReportPdf,
  setAdminToken, getAdminToken, clearAdminToken, adminAcceptances, adminExportAcceptances } from "@/lib/api";
import { toast } from "sonner";
import AuditReportView from "@/components/AuditReportView";
import CostDashboard from "@/components/CostDashboard";
import { Loader2, DollarSign, FileBarChart, Zap, LogOut, ChevronDown, ChevronUp, Send, Download } from "lucide-react";

const DELIVERY_LABEL = {
  pending_review: "Awaiting your review",
  delivered: "Delivered to buyer",
};

function AuditReviewPanel({ report, onSaved }) {
  const [note, setNote] = useState(report.operator_note || "");
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const submit = async (release) => {
    try {
      setBusy(true);
      const res = await adminReviewAuditReport(report.id, { operator_note: note, release });
      toast.success(release ? "Released to buyer" : "Draft saved");
      onSaved({ ...report, operator_note: note, delivery_status: res.delivery_status });
    } catch {
      toast.error("Could not save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      setPdfBusy(true);
      await adminDownloadAuditReportPdf(report.id, `${report.store_domain || "audit"}-margin-audit.pdf`);
    } catch {
      toast.error("Could not build the PDF. Try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="card-cream p-6 md:p-8 mb-6" data-testid={`audit-review-panel-${report.id}`}>
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="tick text-[#9a3412]">The analyst's read — not shown to the buyer until released</div>
        <button onClick={downloadPdf} disabled={pdfBusy} className="btn-secondary shrink-0 disabled:opacity-60" data-testid={`audit-download-pdf-${report.id}`}>
          {pdfBusy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} PDF
        </button>
      </div>
      <textarea
        rows={5}
        className="input-vault resize-y w-full"
        placeholder="Judgment a human adds on top of the computed audit — comparable situations you've seen, whether these numbers are normal for this category/stage, what you'd actually do given who this founder is."
        value={note}
        onChange={(e) => setNote(e.target.value)}
        data-testid={`audit-review-textarea-${report.id}`}
      />
      <div className="flex items-center justify-between gap-4 mt-4">
        <span className="tick">{DELIVERY_LABEL[report.delivery_status] || report.delivery_status}</span>
        <div className="flex gap-3">
          <button onClick={() => submit(false)} disabled={busy} className="btn-secondary disabled:opacity-60" data-testid={`audit-save-draft-${report.id}`}>
            Save draft
          </button>
          <button onClick={() => submit(true)} disabled={busy || !note.trim()} className="btn-primary disabled:opacity-60" data-testid={`audit-release-${report.id}`}>
            <Send size={13} /> Release to buyer
          </button>
        </div>
      </div>
    </div>
  );
}

// Label + which intake field(s) to read, grouped to match the contribution-
// margin waterfall the audit reconciles against. `pct` fields get a % suffix.
const INTAKE_FINANCIAL_GROUPS = [
  {
    title: "Basis",
    rows: [
      ["Currency", "pnl_currency"], ["Period (months)", "reporting_period_months"],
      ["Monthly gross revenue", "monthly_gross_revenue"], ["Monthly order volume", "monthly_order_volume"],
    ],
  },
  {
    title: "CM1 · Unit economics",
    rows: [["AOV", "aov"], ["COGS % of AOV", "cogs_pct_of_aov", true], ["Category mix", "category_mix_notes"]],
  },
  {
    title: "CM2 · Fulfillment & returns",
    rows: [
      ["Return rate", "return_rate_pct", true], ["Cost per return", "cost_per_return"],
      ["Onward shipping / order", "onward_shipping_cost"], ["Packaging / order", "packaging_cost"],
      ["Payment gateway fee", "payment_gateway_fee_pct", true], ["3PL fee / order", "fulfillment_3pl_fee"],
    ],
  },
  {
    title: "CM3 · Marketing",
    rows: [
      ["Monthly marketing spend", "monthly_marketing_spend"], ["Blended ROAS", "blended_roas"],
      ["% revenue paid-attributed", "paid_attributed_revenue_pct", true],
      ["Repeat purchase rate", "repeat_purchase_rate_pct", true],
    ],
  },
  {
    title: "CM4 · Marketplace",
    rows: [
      ["Sells on marketplaces", "sells_on_marketplaces"], ["Which", "marketplace_names"],
      ["% revenue from marketplaces", "marketplace_revenue_share_pct", true],
      ["Marketplace commission", "marketplace_commission_pct", true],
      ["Marketplace ad spend", "marketplace_ad_spend"],
    ],
  },
  {
    title: "Fixed overhead",
    rows: [["Monthly fixed overhead", "monthly_fixed_overhead"], ["Amortized capex", "amortized_capex_notes"]],
  },
];

function IntakeFinancials({ r }) {
  const has = INTAKE_FINANCIAL_GROUPS.some((g) => g.rows.some(([, k]) => r[k] !== undefined && r[k] !== null && r[k] !== ""));
  if (!has) {
    return <p className="font-mono-tech text-[12px] text-[#525252] p-4">No financial data submitted with this intake yet.</p>;
  }
  return (
    <div className="grid md:grid-cols-3 gap-4 p-4">
      {INTAKE_FINANCIAL_GROUPS.map((g) => {
        const present = g.rows.filter(([, k]) => r[k] !== undefined && r[k] !== null && r[k] !== "");
        if (!present.length) return null;
        return (
          <div key={g.title} className="panel-inset border border-[#ddd8ce] bg-[#eeeae2] p-3">
            <div className="tick mb-2 text-[#9a3412]">{g.title}</div>
            <dl className="space-y-1.5">
              {present.map(([label, key, pct]) => (
                <div key={key} className="flex justify-between gap-3 text-[12px]">
                  <dt className="text-[#525252]">{label}</dt>
                  <dd className="text-[#171717] text-right">
                    {typeof r[key] === "boolean" ? (r[key] ? "Yes" : "No") : `${r[key]}${pct ? "%" : ""}`}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Admin surface.
 *
 * Previously gated by Emergent-hosted Google OAuth, which made the operator's
 * own dashboard dependent on a third party's auth service. Now a shared secret
 * held in sessionStorage - it does not survive closing the tab, and the backend
 * fails closed if ADMIN_TOKEN is unset.
 */
function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-cream p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="tick">{label}</span>
        <Icon size={14} className="text-[#9a3412]" />
      </div>
      <div className="font-serif-display text-4xl text-[#171717]">{value}</div>
    </div>
  );
}

function TokenGate({ onUnlock }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return setError("Enter the admin token");
    setAdminToken(value.trim());
    try {
      await adminStats();
      onUnlock();
    } catch {
      clearAdminToken();
      setError("That token was rejected");
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="card-cream p-8 w-full max-w-md" data-testid="admin-gate">
        <div className="tick mb-3 text-[#9a3412]">Restricted</div>
        <h1 className="font-serif-display text-3xl text-[#171717] mb-3">Analyst access</h1>
        <p className="font-mono-tech text-[12px] text-[#525252] leading-relaxed mb-6">
          This surface exposes prospects' domains, their competitor choices, and
          buyer contact details. It is gated by the ADMIN_TOKEN set on the server.
        </p>
        <input
          type="password" className="input-vault mb-3" data-testid="admin-token-input"
          placeholder="Admin token" value={value} autoComplete="off"
          onChange={(e) => { setValue(e.target.value); setError(""); }}
        />
        {error && <p className="font-mono-tech text-[12px] text-[#9a3412] mb-3">{error}</p>}
        <button className="btn-primary w-full justify-center" data-testid="admin-unlock">Unlock</button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [unlocked, setUnlocked] = useState(!!getAdminToken());
  const [state, setState] = useState({
    stats: null, tx: [], sweeps: [], intakes: [], enquiries: [], auditReports: [], acceptances: [], busy: true, error: null,
  });
  const [expandedIntake, setExpandedIntake] = useState(null);
  const [expandedAuditReport, setExpandedAuditReport] = useState(null);

  useEffect(() => {
    if (!unlocked) return;
    let cancelled = false;
    Promise.all([adminStats(), adminTransactions(), adminSweeps(), adminIntakes(), adminEnquiries(), adminAuditReports(), adminAcceptances()])
      .then(([stats, tx, sweeps, intakes, enquiries, auditReports, acc]) => {
        if (!cancelled) setState({ stats, tx, sweeps, intakes, enquiries, auditReports, acceptances: acc?.acceptances || [], busy: false, error: null });
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 401) { clearAdminToken(); setUnlocked(false); return; }
        setState((s) => ({ ...s, busy: false, error: "Could not load admin data" }));
      });
    return () => { cancelled = true; };
  }, [unlocked]);

  if (!unlocked) return <TokenGate onUnlock={() => setUnlocked(true)} />;

  const { stats, tx, sweeps, intakes, enquiries, auditReports, acceptances, busy, error } = state;
  const lockOut = () => { clearAdminToken(); setUnlocked(false); };
  const pendingReviewCount = auditReports.filter((r) => r.delivery_status !== "delivered").length;
  const updateAuditReport = (updated) =>
    setState((s) => ({ ...s, auditReports: s.auditReports.map((r) => (r.id === updated.id ? updated : r)) }));

  if (busy) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#9a3412]" size={22} />
      </div>
    );
  }

  return (
    <>
      <Nav />
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 pt-32 pb-24 space-y-8">
        <SegmentFinder />
        <header className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="tick mb-2 text-[#9a3412]">Analyst</div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-[#171717]">Operations</h1>
          </div>
          <button onClick={lockOut} className="btn-secondary" data-testid="admin-lock">
            <LogOut size={13} /> Lock
          </button>
        </header>

        {error && <p className="font-mono-tech text-[13px] text-[#9a3412]">{error}</p>}

        <div className="grid md:grid-cols-4 gap-6">
          <Stat testid="admin-stat-revenue" icon={DollarSign} label="Revenue"
                value={stats?.revenue != null ? `A$${stats.revenue.toFixed(2)}` : "—"} />
          <Stat testid="admin-stat-paid" icon={Zap} label="Paid audits" value={stats?.total_paid ?? "—"} />
          <Stat testid="admin-stat-sweeps" icon={FileBarChart} label="Sweeps run" value={stats?.total_sweeps ?? "—"} />
          <Stat testid="admin-stat-intakes" icon={FileBarChart} label="Intakes received" value={intakes.length} />
        </div>

        <section className="card-cream p-5 md:p-8" data-testid="admin-costs">
          <CostDashboard />
        </section>

        <section className="card-cream" data-testid="admin-audit-reports">
          <header className="border-b border-[#ddd8ce] p-5 flex items-center justify-between">
            <span className="tick">Margin audits · computed, awaiting your read</span>
            <span className="tick text-[#9a3412]">{pendingReviewCount} pending / {auditReports.length} total</span>
          </header>
          {auditReports.length === 0 ? (
            <p className="p-6 font-mono-tech text-[12px] text-[#525252]">No audits generated yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full font-mono-tech text-[12px]">
                <thead><tr className="text-left border-b border-[#ddd8ce]">
                  {["When","Business","Domain","Competitors","Status",""].map((h) =>
                    <th key={h} className="p-4 tick">{h}</th>)}
                </tr></thead>
                <tbody>
                  {auditReports.map((r) => (
                    <React.Fragment key={r.id}>
                      <tr className="border-b border-[#e8e4df] align-top">
                        <td className="p-4 text-[#525252] whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                        <td className="p-4 text-[#171717]">{r.business_name}</td>
                        <td className="p-4 text-[#525252]">{r.store_domain}</td>
                        <td className="p-4 text-[#525252]">{(r.competitor_domains || []).join(", ") || "—"}</td>
                        <td className={`p-4 ${r.delivery_status === "delivered" ? "text-[#3f6b4a]" : "text-[#9a3412] font-medium"}`}>
                          {DELIVERY_LABEL[r.delivery_status] || r.delivery_status}
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => setExpandedAuditReport((id) => (id === r.id ? null : r.id))}
                            className="tick text-[#9a3412] flex items-center gap-1"
                            data-testid={`audit-report-toggle-${r.id}`}
                          >
                            Review {expandedAuditReport === r.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          </button>
                        </td>
                      </tr>
                      {expandedAuditReport === r.id && (
                        <tr className="border-b border-[#e8e4df] bg-[#f5f2ed]">
                          <td colSpan={6} className="p-6">
                            <AuditReviewPanel report={r} onSaved={updateAuditReport} />
                            <AuditReportView report={r} embedded />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card-cream" data-testid="admin-intakes">
          <header className="border-b border-[#ddd8ce] p-5 flex items-center justify-between">
            <span className="tick">Audit intakes · awaiting the analyst</span>
            <span className="tick text-[#9a3412]">{intakes.length}</span>
          </header>
          {intakes.length === 0 ? (
            <p className="p-6 font-mono-tech text-[12px] text-[#525252]">No paid audits awaiting work.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full font-mono-tech text-[12px]">
                <thead><tr className="text-left border-b border-[#ddd8ce]">
                  {["When","Business","Domain","Category","Platform","P&L from","Contact","Question",""].map((h) =>
                    <th key={h} className="p-4 tick">{h}</th>)}
                </tr></thead>
                <tbody>
                  {intakes.map((r) => (
                    <React.Fragment key={r.id}>
                      <tr className="border-b border-[#e8e4df] align-top">
                        <td className="p-4 text-[#525252] whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                        <td className="p-4 text-[#171717]">{r.business_name}</td>
                        <td className="p-4 text-[#525252]">{r.store_domain}</td>
                        <td className="p-4 text-[#525252]">{r.business_subtype || r.business_category || "—"}</td>
                        <td className="p-4 text-[#525252]">{r.platform || "—"}</td>
                        <td className="p-4 text-[#525252]">{r.pnl_format || "—"}</td>
                        <td className="p-4 text-[#525252]">{r.contact_email}</td>
                        <td className="p-4 text-[#525252] max-w-[280px]">{r.primary_question || "—"}</td>
                        <td className="p-4">
                          <button
                            onClick={() => setExpandedIntake((id) => (id === r.id ? null : r.id))}
                            className="tick text-[#9a3412] flex items-center gap-1"
                            data-testid={`intake-financials-toggle-${r.id}`}
                          >
                            Financials {expandedIntake === r.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          </button>
                        </td>
                      </tr>
                      {expandedIntake === r.id && (
                        <tr className="border-b border-[#e8e4df] bg-[#f5f2ed]">
                          <td colSpan={9}><IntakeFinancials r={r} /></td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card-cream" data-testid="admin-enquiries">
          <header className="border-b border-[#ddd8ce] p-5 flex items-center justify-between">
            <span className="tick">Retainer enquiries</span>
            <span className="tick text-[#9a3412]">{enquiries.length}</span>
          </header>
          {enquiries.length === 0 ? (
            <p className="p-6 font-mono-tech text-[12px] text-[#525252]">No enquiries yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full font-mono-tech text-[12px]">
                <thead><tr className="text-left border-b border-[#ddd8ce]">
                  {["When","Name","Email","Domain","Revenue","Message"].map((h) =>
                    <th key={h} className="p-4 tick">{h}</th>)}
                </tr></thead>
                <tbody>
                  {enquiries.map((r) => (
                    <tr key={r.id} className="border-b border-[#e8e4df] align-top">
                      <td className="p-4 text-[#525252] whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="p-4 text-[#171717]">{r.name}</td>
                      <td className="p-4 text-[#525252]">{r.email}</td>
                      <td className="p-4 text-[#525252]">{r.store_domain || "—"}</td>
                      <td className="p-4 text-[#525252]">{r.revenue_band || "—"}</td>
                      <td className="p-4 text-[#525252] max-w-[320px]">{r.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card-cream" data-testid="admin-sweeps">
          <header className="border-b border-[#ddd8ce] p-5 flex items-center justify-between">
            <span className="tick">All sweeps</span><span className="tick text-[#9a3412]">{sweeps.length}</span>
          </header>
          <div className="overflow-x-auto">
            <table className="w-full font-mono-tech text-[12px]">
              <thead><tr className="text-left border-b border-[#ddd8ce]">
                {["When","Your domain","Competitor","Email"].map((h) => <th key={h} className="p-4 tick">{h}</th>)}
              </tr></thead>
              <tbody>
                {sweeps.map((r) => (
                  <tr key={r.id} className="border-b border-[#e8e4df]">
                    <td className="p-4 text-[#525252] whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="p-4 text-[#171717]">{r.your_domain}</td>
                    <td className="p-4 text-[#525252]">{r.competitor_domain}</td>
                    <td className="p-4 text-[#525252]">{r.email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card-cream" data-testid="admin-acceptances">
          <div className="p-5 border-b border-[#ddd8ce] flex items-center justify-between gap-4">
            <div>
              <div className="tick text-[#9a3412]">Terms acceptance</div>
              <p className="mt-1 font-mono-tech text-[11px] text-[#525252]">
                Append-only. Each row is one document accepted by one person at one version.
              </p>
            </div>
            <button onClick={() => adminExportAcceptances()} className="btn-secondary shrink-0"
                    data-testid="admin-export-acceptances">
              <Download size={13} /> Export CSV
            </button>
          </div>
          {acceptances.length === 0 ? (
            <p className="p-5 font-mono-tech text-[12px] text-[#525252]">No acceptances recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] font-mono-tech">
                <thead>
                  <tr className="border-b border-[#ddd8ce] text-[#525252]">
                    {["When", "Who", "Context", "Document", "Version", "IP"].map((h) => (
                      <th key={h} className="text-left p-3 font-normal">{h}</th>
                    ))}
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {acceptances.slice(0, 60).map((a) => (
                    <tr key={a.id} className="border-b border-[#e6e1d8] text-[#404040]">
                      <td className="p-3 whitespace-nowrap">{String(a.accepted_at).slice(0, 19).replace("T", " ")}</td>
                      <td className="p-3">{a.user_email || (a.user_id ? a.user_id.slice(0, 12) : "anonymous")}</td>
                      <td className="p-3">{a.context}</td>
                      <td className="p-3">{a.document_slug}</td>
                      <td className="p-3">{a.document_version}</td>
                      <td className="p-3">{a.ip_address}</td>
                      <td className="p-3 text-right">
                        {a.user_id && (
                          <button onClick={() => adminExportAcceptances(a.user_id)}
                                  className="text-[#9a3412] underline underline-offset-2">
                            Export
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card-cream" data-testid="admin-transactions">
          <header className="border-b border-[#ddd8ce] p-5 flex items-center justify-between">
            <span className="tick">Transactions</span><span className="tick text-[#9a3412]">{tx.length}</span>
          </header>
          <div className="overflow-x-auto">
            <table className="w-full font-mono-tech text-[12px]">
              <thead><tr className="text-left border-b border-[#ddd8ce]">
                {["When","Package","Amount","Email","Status"].map((h) => <th key={h} className="p-4 tick">{h}</th>)}
              </tr></thead>
              <tbody>
                {tx.map((t) => (
                  <tr key={t.id} className="border-b border-[#e8e4df]">
                    <td className="p-4 text-[#525252] whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="p-4 text-[#171717]">{t.package_name}</td>
                    <td className="p-4 text-[#9a3412]">${t.amount?.toFixed(2)}</td>
                    <td className="p-4 text-[#525252]">{t.email || "—"}</td>
                    <td className={`p-4 ${t.payment_status === "paid" ? "text-[#9a3412] font-medium" : "text-[#525252]"}`}>
                      {t.payment_status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </>
  );
}
